import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useFavoriteStore, useLeadStore, useLeaseCompStore, usePipedriveStore, useSaleCompStore, useSavedViewStore, useTenantInMarketStore } from "../..";
import { FavoriteTargetType } from "../../interfaces/IFavorite";
import CompanyInfoWithIconAndNavigation from "../companies/CompanyInfoWithIconAndNavigation";
import DealInfoWithIconAndNavigation from "../deals/DealInfoWithIconAndNavigation";
import LeadInfoWithIconAndNavigation from "../leads/LeadInfoWithIconAndNavigation";
import LeaseCompInfoWithIconAndNavigation from "../leaseComps/LeaseCompInfoWithIconAndNavigation";
import PropertyInfoWithIconAndNavigation from "../property/PropertyInfoWithIconAndNavigation";
import SaleCompInfoWithIconAndNavigation from "../saleComps/SaleCompInfoWithIconAndNavigation";
import SavedViewWithPreviewAndDelete from "../savedViews/SavedViewInfoWithPreviewAndDelete";
import TenantInMarketInfoWithIconAndNavigation from "../tenantsInMarket/TenantInMarketInfoWithIconAndNavigation";

function FavoriteInfo({ targetId, targetType, className }: IFavoriteInfoProps) {
    const favoriteStore = useFavoriteStore();
    const leadStore = useLeadStore();
    const leaseCompStore = useLeaseCompStore();
    const saleCompStore = useSaleCompStore();
    const pipedriveStore = usePipedriveStore();
    const tenantInMarketStore = useTenantInMarketStore();
    const savedViewStore = useSavedViewStore();
    const favoritePropertyDetails = useMemo(() => computed(() => targetType === FavoriteTargetType.Property ? favoriteStore.favoritePropertiesDetails.find(fpd => fpd.id === targetId) : null), [targetId, targetType]).get();
    const deal = useMemo(() => computed(() => targetType === FavoriteTargetType.Deal ? pipedriveStore.deals.find(d => d.id === targetId) : null), [targetId, targetType]).get();
    const lead = useMemo(() => computed(() => targetType === FavoriteTargetType.Lead ? leadStore.leads.find(l => l.id === targetId) : null), [targetId, targetType]).get();
    const leaseComp = useMemo(() => computed(() => targetType === FavoriteTargetType.LeaseComp ? leaseCompStore.leaseComps.find(lc => lc.id === targetId) : null), [targetId, targetType]).get();
    const saleComp = useMemo(() => computed(() => targetType === FavoriteTargetType.SaleComp ? saleCompStore.saleComps.find(sc => sc.id === targetId) : null), [targetId, targetType]).get();
    const tenantInMarket = useMemo(() => computed(() => targetType === FavoriteTargetType.TenantInMarket ? tenantInMarketStore.tenantsInMarket.find(tim => tim.id === targetId) : null), [targetId, targetType]).get();
    const company = useMemo(() => computed(() => targetType === FavoriteTargetType.Company ? pipedriveStore.organizations.find(o => o.id === targetId) : null), [targetId, targetType]).get();
    const savedView = useMemo(() => computed(() => targetType === FavoriteTargetType.SavedView ? savedViewStore.savedViews.find(sv => sv.id === targetId) : null), [targetId, targetType]).get();

    if (!deal && !lead && !leaseComp && !saleComp && !favoritePropertyDetails && !tenantInMarket && !company && !savedView)
        return null;

    return (
        <>
            {targetType === FavoriteTargetType.Property && (
                <PropertyInfoWithIconAndNavigation
                    longitude={favoritePropertyDetails!.centerPoint.longitude}
                    latitude={favoritePropertyDetails!.centerPoint.latitude}
                    address={favoritePropertyDetails!.address}
                    city={favoritePropertyDetails!.city}
                    state={favoritePropertyDetails!.state}
                    zip={favoritePropertyDetails!.zip}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.Deal && (
                <DealInfoWithIconAndNavigation
                    id={deal!.id}
                    name={deal!.name}
                    value={deal!.value}
                    status={deal!.status}
                    stageId={deal!.dealStageId}
                    buildingSize={deal!.buildingSize}
                    landSizeAcres={deal!.landSizeAcres}
                    zoning={deal!.zoning}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.Lead && (
                <LeadInfoWithIconAndNavigation
                    id={lead!.id}
                    address={lead!.address}
                    ownerName={lead!.ownerName}
                    status={lead!.status}
                    buildingSize={lead!.buildingSize}
                    landSizeAcres={lead!.landSize}
                    zoning={lead!.zoning}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.LeaseComp && (
                <LeaseCompInfoWithIconAndNavigation
                    id={leaseComp!.id}
                    pipedriveCompanyId={leaseComp!.pipedriveCompanyId}
                    address={leaseComp!.address}
                    totalRent={leaseComp!.totalRent}
                    parcelSizeAcres={leaseComp!.parcelSizeAcres}
                    buildingSize={leaseComp!.buildingSize}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.SaleComp && (
                <SaleCompInfoWithIconAndNavigation
                    id={saleComp!.id}
                    address={saleComp!.address}
                    salePrice={saleComp!.salePrice}
                    pipedriveBuyerId={saleComp!.pipedriveBuyerId}
                    buildingSize={saleComp!.buildingSize}
                    parcelSizeAcres={saleComp!.parcelSizeAcres}
                    zoning={saleComp!.zoning}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.TenantInMarket && (
                <TenantInMarketInfoWithIconAndNavigation
                    id={tenantInMarket!.id}
                    description={tenantInMarket!.description}
                    landAreaMin={tenantInMarket!.landAreaMin}
                    landAreaMax={tenantInMarket!.landAreaMax}
                    buildingAreaMin={tenantInMarket!.buildingAreaMin}
                    buildingAreaMax={tenantInMarket!.buildingAreaMax}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.Company && (
                <CompanyInfoWithIconAndNavigation
                    id={company!.id}
                    name={company!.name}
                    className={className}
                />
            )}
            {targetType === FavoriteTargetType.SavedView && (
                <SavedViewWithPreviewAndDelete
                    id={savedView!.id}
                    name={savedView!.name}
                    isShared={savedView!.isShared}
                    modifiedDate={savedView!.modifiedDate}
                    createdBy={savedView!.createdByUser.userName}
                    className={className}
                />
            )}
        </>
    );
}

interface IFavoriteInfoProps {
    targetId: number;
    targetType: FavoriteTargetType;
    className?: string;
}

export default observer(FavoriteInfo);